import { HttpHeaders, HttpResponse, HttpResponseBase } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ProblemDetails } from "./service-shared";
import { mergeMap as _observableMergeMap, mergeMap, catchError as _observableCatch, catchError } from 'rxjs/operators';
import { Observable, throwError as _observableThrow, throwError, of as _observableOf, from, of } from 'rxjs';
import {
    CreateApplicationResponse,
    ICreateApplicationInput,
    GetAssignedApplicationsResponse,
    GetApplicationResponse,
    ISaveApplicationInput,
    SaveApplicationResponse,
    SubmitApplicationResponse,
    ISubmitApplicationInput,
    IValidateApplicationInput,
    ValidateApplicationResponse,
    FindApplicationsResponse,
    IFindApplicationsInput,
    GetApplicationHistoryResponse,
    GetApplicationSummaryResponse,
    IGetApplicationSummaryInput,
    ISaveApplicationAuthenticationInput,
    ISaveApplicationBySearchControlInput,
    SaveApplicationBySearchControlResponse,
    LastVisitedPage,
    GetApplicationGuestResponse,
    ISurveyResultInput,
    GetApplicationCountResponse
} from "./model/application-service.model";
import { blobToText, processProblemResponse, throwException } from "./service-functions";
import { CsvExportState } from "app/modules/dashboard/dashboard.model";
import { ServiceProxyBase } from "./_service-proxy-base";
import { SurveyResultResponse } from "./model/form-service.model";

@Injectable({ providedIn: 'root' })
export class ApplicationsServiceProxy extends ServiceProxyBase {
    /**
     * Gets all applications the user has access to.
     * @return Success
     */
    getAssignedApplications(): Observable<GetAssignedApplicationsResponse> {
        let url_ = this.dashboardUrl + "/GetAssignedApplications";
        url_ = url_.replace(/[?&]$/, "");

        let options_: any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.createHttpObserver(GetAssignedApplicationsResponse, "post", url_, options_);
    }

    getApplicationCount(): Observable<GetApplicationCountResponse>
    {
        let url_ = this.dashboardUrl + "/CountApplications";
        let options_: any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.createHttpObserver(GetApplicationCountResponse, "post", url_, options_);
    }

    /**
     * Creates a new application for the given Product ID.
     * @param formId The form identifier.
     * @return Success
     */
    createApplication(data: ICreateApplicationInput): Observable<CreateApplicationResponse> {
        let url_ = this.baseUrl + "/CreateApplication";

        if (data.formId === undefined || data.formId === null)
            throw new Error("The parameter 'formId' must be defined.");

        let content_ = JSON.stringify(data);

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "application/json, text/plain, */*",
                "Content-Type": "application/json;charset=utf-8",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processCreateApplication(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processCreateApplication(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<CreateApplicationResponse>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<CreateApplicationResponse>;
        }));
    }

    protected processCreateApplication(response: HttpResponseBase): Observable<CreateApplicationResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = CreateApplicationResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        } else if (status === 401) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result401: any = null;
                let resultData401 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result401 = ProblemDetails.fromJS(resultData401);
                return throwException("Unauthorized", status, _responseText, _headers, result401);
            }));
        } else if (status === 403) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result403: any = null;
                let resultData403 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result403 = ProblemDetails.fromJS(resultData403);
                return throwException("Forbidden", status, _responseText, _headers, result403);
            }));
        } else if (status === 404) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result404: any = null;
                let resultData404 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result404 = ProblemDetails.fromJS(resultData404);
                return throwException("Not Found", status, _responseText, _headers, result404);
            }));
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                return throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<CreateApplicationResponse>(null as any);
    }

    getSurveyResult(data: ISurveyResultInput): Observable<SurveyResultResponse> {
        if (data.formId == null) {
            throw new Error("The parameter 'formId' must be defined.");
        }

        if (data.applicationData == null) {
            throw new Error("The parameter 'applicationData' must be defined.");
        }

        let url_ = (this.baseUrl + "/GetSurveyResult").replace(/[?&]$/, "");

        let content_ = JSON.stringify(data);

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "json",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.createHttpObserver(SurveyResultResponse, "post", url_, options_);
    }

    /**
     * Gets the application specified by the id.
     * @param applicationId The application identifier.
     * @return Success
     */
    getApplication(applicationId: string, versionNumber: number): Observable<GetApplicationResponse> {
        if (applicationId === undefined || applicationId === null) {
            throw new Error("The parameter 'applicationId' must be defined.");
        }

        let url_ = this.baseUrl + "/GetApplication";
        url_ = url_.replace(/[?&]$/, "");

        let content_ = JSON.stringify({ "applicationId": applicationId, "versionNumber": null });

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "application/json, text/plain, */*",
                "Content-Type": "application/json;charset=utf-8",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processGetApplication(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetApplication(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<GetApplicationResponse>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<GetApplicationResponse>;
        }));
    }

    protected processGetApplication(response: HttpResponseBase): Observable<GetApplicationResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {};

        if (response.headers) {
            for (let key of response.headers.keys()) {
                _headers[key] = response.headers.get(key);
            }
        }

        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = GetApplicationResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        } else if (status === 401) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result401: any = null;
                let resultData401 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result401 = ProblemDetails.fromJS(resultData401);
                return throwException("Unauthorized", status, _responseText, _headers, result401);
            }));
        } else if (status === 403) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result403: any = null;
                let resultData403 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result403 = ProblemDetails.fromJS(resultData403);
                return throwException("Forbidden", status, _responseText, _headers, result403);
            }));
        } else if (status === 404) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result404: any = null;
                let resultData404 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result404 = ProblemDetails.fromJS(resultData404);
                return throwException("Not Found", status, _responseText, _headers, result404);
            }));
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                return throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<GetApplicationResponse>(null as any);
    }

    /**
     * Saves the application.
     * @param body (optional) The application.
     * @return Success
     */
    saveApplication(body: ISaveApplicationInput): Observable<void | SaveApplicationResponse> {
        let url_ = this.baseUrl + "/SaveApplication";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_)
            .pipe(_observableMergeMap((response_: any) => {
                return this.processSaveApplication(response_);
            })).pipe(catchError((response_: any) => {
                if (response_ instanceof HttpResponseBase) {
                    try {
                        return this.processSaveApplication(response_ as any);
                    } catch (e) {
                        return throwError(() => new Error(e));
                    }
                } else
                    return throwError(() => new Error(response_));
            }));
    }

    protected processSaveApplication(response: HttpResponseBase): Observable<SaveApplicationResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = SaveApplicationResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<SaveApplicationResponse>(null as any);
    }

    /**
     * Submits the application.
     * @param body (optional) The application.
     * @return Success
     */
    submitApplication(body: ISubmitApplicationInput | undefined): Observable<SubmitApplicationResponse> {
        let url_ = this.baseUrl + "/SubmitApplication";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_)
            .pipe(
                mergeMap((response_: any) => {
                    return this.processSubmitApplication(response_);
                }),
                catchError((response_: any) => {
                    if (response_ instanceof HttpResponseBase) {
                        try {
                            return this.processSubmitApplication(response_ as any);
                        } catch (e) {
                            return throwError(() => new Error(e));
                        }
                    } else
                        return throwError(() => new Error(response_));
                }));
    }

    protected processSubmitApplication(response: HttpResponseBase): Observable<SubmitApplicationResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = SubmitApplicationResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<SubmitApplicationResponse>(null as any);
    }

    /**
     * Validates the application.
     * @param body (optional) The application.
     * @return Success
     */
    validateApplication(body: IValidateApplicationInput | undefined): Observable<void | ValidateApplicationResponse> {
        let url_ = this.baseUrl + "/ValidateApplication";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processValidateApplication(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processValidateApplication(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<void>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<void>;
        }));
    }

    protected processValidateApplication(response: HttpResponseBase): Observable<ValidateApplicationResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = ValidateApplicationResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<ValidateApplicationResponse>(null as any);
    }

    findApplications(body: IFindApplicationsInput | undefined): Observable<void | FindApplicationsResponse> {
        let url_ = this.dashboardUrl + "/FindApplications";
        url_ = url_.replace(/[?&]$/, "");

        let data = {
            formId: body.formId,
            filter: JSON.stringify(body.filter),
            pagination: JSON.stringify(body.pagination)
        };
        const content_ = JSON.stringify(data);

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processFindApplications(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processFindApplications(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<void>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<void>;
        }));
    }

    protected processFindApplications(response: HttpResponseBase): Observable<FindApplicationsResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = FindApplicationsResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<FindApplicationsResponse>(null as any);
    }

    getApplicationHistory(applicationId: string): Observable<void | GetApplicationHistoryResponse> {
        let url_ = (this.dashboardUrl + "/GetApplicationHistory").replace(/[?&]$/, "");
        let content_ = JSON.stringify({ applicationId: applicationId });
        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "json",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.createHttpObserver(GetApplicationHistoryResponse, "post", url_, options_);
    }

    getApplicationsSummary(body: IGetApplicationSummaryInput): Observable<void | GetApplicationSummaryResponse> {
        let url_ = this.dashboardUrl + "/GetApplicationsSummary";
        url_ = url_.replace(/[?&]$/, "");
        const content_ = JSON.stringify(body);

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processGetApplicationsSummary(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetApplicationsSummary(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<void>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<void>;
        }));
    }

    protected processGetApplicationsSummary(response: HttpResponseBase): Observable<GetApplicationSummaryResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = GetApplicationSummaryResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<GetApplicationSummaryResponse>(null as any);
    }

    saveApplicationAuthentication(data: ISaveApplicationAuthenticationInput): Observable<GetApplicationResponse> {
        let url_ = this.baseUrl + "/SaveApplicationAuthentication";
        url_ = url_.replace(/[?&]$/, "");

        let content_ = JSON.stringify(data);

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "application/json, text/plain, */*",
                "Content-Type": "application/json;charset=utf-8",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processSaveApplicationAuthentication(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processSaveApplicationAuthentication(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<GetApplicationResponse>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<GetApplicationResponse>;
        }));
    }

    protected processSaveApplicationAuthentication(response: HttpResponseBase): Observable<GetApplicationResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = GetApplicationResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        } else if (status === 401) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result401: any = null;
                let resultData401 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result401 = ProblemDetails.fromJS(resultData401);
                return throwException("Unauthorized", status, _responseText, _headers, result401);
            }));
        } else if (status === 403) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result403: any = null;
                let resultData403 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result403 = ProblemDetails.fromJS(resultData403);
                return throwException("Forbidden", status, _responseText, _headers, result403);
            }));
        } else if (status === 404) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result404: any = null;
                let resultData404 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result404 = ProblemDetails.fromJS(resultData404);
                return throwException("Not Found", status, _responseText, _headers, result404);
            }));
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                return throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<GetApplicationResponse>(null as any);
    }

    saveApplicationBySearchControl(body: ISaveApplicationBySearchControlInput): Observable<void | SaveApplicationBySearchControlResponse> {
        let url_ = this.baseUrl + "/SaveApplicationBySearchControl";
        url_ = url_.replace(/[?&]$/, "");
        const content_ = JSON.stringify(body);

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processSaveApplicationBySearchControl(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processSaveApplicationBySearchControl(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<void>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<void>;
        }));
    }

    protected processSaveApplicationBySearchControl(response: HttpResponseBase): Observable<SaveApplicationBySearchControlResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = SaveApplicationBySearchControlResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<SaveApplicationBySearchControlResponse>(null as any);
    }

    //GetApplicationGuests
    getApplicationGuests(applicationId: string): Observable<void | GetApplicationGuestResponse> {
        let url_ = this.baseUrl + "/GetApplicationGuests";
        url_ = url_.replace(/[?&]$/, "");

        let options_: any = {
            body: { applicationId: applicationId },
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processGetApplicationGuests(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetApplicationGuests(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<void>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<void>;
        }));
    }

    protected processGetApplicationGuests(response: HttpResponseBase): Observable<GetApplicationGuestResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = GetApplicationGuestResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<GetApplicationGuestResponse>(null as any);
    }

    //ExpireGuestAccess
    expireGuestAccess(applicationId: string, guestUserId: string): Observable<void | {success: boolean}> {
        let url_ = this.baseUrl + "/ExpireGuestAccess";
        url_ = url_.replace(/[?&]$/, "");
        const content_ = JSON.stringify({
            applicationId,
            guestUserId
        });

        let options_: any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processExpireGuestAccess(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processExpireGuestAccess(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<void>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<void>;
        }));
    }

    protected processExpireGuestAccess(response: HttpResponseBase): Observable<{success: boolean}> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: {success: boolean} = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = !!resultData200 ? { success: !!resultData200['Success'] } : { success: false }
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<GetApplicationGuestResponse>(null as any);
    }

    //CalculateAll

    //ComposeHtmlContent

    //SetupPayment

    //PayWaySingleUseToken

    //GetLastVisitedPage

    getLastVisitedPage(applicationId: string): Observable<void | LastVisitedPage> {
        let url_ = this.baseUrl + "/GetLastVisitedPage";
        url_ = url_.replace(/[?&]$/, "");

        let options_: any = {
            body: { applicationId: applicationId, },
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "application/json, text/plain, */*",
                "Content-Type": "application/json;charset=utf-8",
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processLastVisitedPage(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processLastVisitedPage(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<LastVisitedPage>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<LastVisitedPage>;
        }));
    }

    protected processLastVisitedPage(response: HttpResponseBase): Observable<LastVisitedPage> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = LastVisitedPage.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<LastVisitedPage>(null as any);
    }

    exportCsv(state: CsvExportState): Observable<void | string> {
        const formName = state.fileName.replace(' ', '_') + '_Export';
        let url_ = this.baseCsvUrl + '?formId=' + state.formId + '&fileName=' + formName +
            '&filter=' + JSON.stringify(state.filter);

        let options_: any = {
            body: JSON.stringify(state),
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("get", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processExportCsv(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processExportCsv(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<void>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<void>;
        }));
    }

    protected processExportCsv(response: HttpResponseBase): Observable<string> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } };
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let _responseJson = JSON.stringify(_responseText);
                let result200: any = null;
                let resultData200 = _responseJson === "" ? null : typeof _responseText === 'string' ? _responseText : JSON.parse(_responseJson, this.jsonParseReviver);
                result200 = resultData200;
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<string>(null as any);
    }
}
